import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TradeLimit } from '@shared/protos/user';
import { useApi } from '@hooks/useApi';
import { useState, useCallback, useMemo } from 'react';
import { useUpdateUserSettings, useUserSettings } from '@shared/contexts/UserContextProvider';
import { TradingNumberInput } from '@features/dashboard/widgets/trading-v2/components/TradingNumberInput';
import { DEFAULT_TRADE_SIZE } from '@features/dashboard/widgets/trading-v2/constants';

interface TradingPreferenceDialogProps {
  open: boolean;
  onClose: (updatedLimits?: TradeLimit[]) => void;
  tradeLimits: TradeLimit[];
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  cursor: 'grab',
  transition: 'transform 250ms ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    cursor: 'grabbing',
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

const StyledPaper = styled(Paper)(() => ({
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
}));

interface ExchangeItemProps {
  exchange: string;
  index: number;
  productIndex: number;
}

const ExchangeItem: React.FC<ExchangeItemProps> = ({ exchange, index, productIndex }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${productIndex}-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <StyledListItem ref={setNodeRef} style={style} {...attributes} {...listeners} sx={{ py: 0.5, px: 1 }}>
      <ListItemText
        primary={exchange.toUpperCase()}
        secondary={`Position ${index + 1}`}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </StyledListItem>
  );
};

interface ProductItemProps {
  limit: TradeLimit;
  index: number;
  localDefaultSizes: Map<string, number | undefined>;
  onDefaultSizeUpdate: (productIndex: number, value: number | undefined) => void;
  isDefaultSizeValid: boolean;
}

const ProductItem: React.FC<ProductItemProps> = ({ limit, index, localDefaultSizes, onDefaultSizeUpdate, isDefaultSizeValid }) => {
  return (
    <StyledPaper
      sx={{
        p: 1.5,
        height: 'fit-content',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
        {limit.symbol.toUpperCase()} - Limit: {limit.limit}
      </Typography>

      <TradingNumberInput
        label="Default Trade Size"
        value={localDefaultSizes.get(limit.symbol)}
        allowClearable
        sizeSmall
        error={!isDefaultSizeValid}
        max={Number(limit.limit)}
        min={DEFAULT_TRADE_SIZE}
        sx={{ width: '100%', mt: 1, mb: 1 }}
        onChange={(event, value) => onDefaultSizeUpdate(index, value)}
      />

      <List dense sx={{ py: 0 }}>
        <SortableContext items={limit.exchange.map((limit, exchangeIndex) => `${index}-${exchangeIndex}`)} strategy={verticalListSortingStrategy}>
          {limit.exchange.map((exchange, exchangeIndex) => (
            <ExchangeItem key={`${exchange}-${exchangeIndex}`} exchange={exchange} index={exchangeIndex} productIndex={index} />
          ))}
        </SortableContext>
      </List>
    </StyledPaper>
  );
};

export const TradingPreferenceDialog: React.FC<TradingPreferenceDialogProps> = ({ open, onClose, tradeLimits }) => {
  const { apiClient } = useApi();
  const userSettings = useUserSettings();
  const updateUserSettings = useUpdateUserSettings();
  const [localTradeLimits, setLocalTradeLimits] = useState<TradeLimit[]>(tradeLimits);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [localDefaultSizes, setLocalDefaultSizes] = useState<Map<string, number | undefined>>(
    new Map(tradeLimits.map(limit => [limit.symbol, userSettings?.ocl_trading?.default_trade_sizes?.[limit.symbol] ?? DEFAULT_TRADE_SIZE]))
  );

  const onExchangeReorder = useCallback(
    (productIndex: number, oldIndex: number, newIndex: number) => {
      const updatedLimits = [...localTradeLimits];
      const product = { ...updatedLimits[productIndex] };
      const exchanges = [...product.exchange];

      const reorderedExchanges = arrayMove(exchanges, oldIndex, newIndex);
      product.exchange = reorderedExchanges;
      updatedLimits[productIndex] = product;
      setLocalTradeLimits(updatedLimits);
    },
    [localTradeLimits]
  );

  const onDefaultSizeUpdate = useCallback(
    (productIndex: number, value: number | undefined) => {
      const updatedDefaultSizes: Map<string, number | undefined> = new Map(localDefaultSizes);
      updatedDefaultSizes.set(localTradeLimits[productIndex].symbol, value);
      setLocalDefaultSizes(updatedDefaultSizes);
    },
    [localTradeLimits, localDefaultSizes]
  );

  const isDefaultSizeValid = useCallback(
    (productIndex: number) => {
      const defaultSize = localDefaultSizes.get(localTradeLimits[productIndex].symbol);
      return (
        defaultSize !== undefined && defaultSize >= DEFAULT_TRADE_SIZE && defaultSize <= localTradeLimits[productIndex].limit && !isNaN(defaultSize)
      );
    },
    [localTradeLimits, localDefaultSizes]
  );

  const isDefaultSizeValidAll = useMemo(() => {
    return localTradeLimits.every((limit, index) => isDefaultSizeValid(index));
  }, [localTradeLimits, isDefaultSizeValid]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      const activeParts = activeId.split('-');
      const overParts = overId.split('-');

      const activeProductIndex = parseInt(activeParts[0]);
      const activeExchangeIndex = parseInt(activeParts[1]);
      const overProductIndex = parseInt(overParts[0]);
      const overExchangeIndex = parseInt(overParts[1]);

      if (activeProductIndex === overProductIndex) {
        onExchangeReorder(activeProductIndex, activeExchangeIndex, overExchangeIndex);
      }
    },
    [onExchangeReorder]
  );

  const handleSave = useCallback(async () => {
    if (!apiClient) return;

    setIsSaving(true);
    try {
      const currentSettings = await apiClient.getUserSettings();
      if (!currentSettings) return;

      const defaultTradeSizes = Object.fromEntries(
        Array.from(localDefaultSizes.entries())
          .filter(([_, value]) => value !== undefined)
          .map(([key, value]) => [key, value])
      ) as Record<string, number>;
      await updateUserSettings?.({
        ...currentSettings,
        ocl_trading: {
          ...currentSettings.ocl_trading,
          trade_limits: localTradeLimits,
          default_trade_sizes: defaultTradeSizes,
        },
      });
      onClose(localTradeLimits);
    } catch (error) {
      console.error('Failed to save exchange order:', error);
    } finally {
      setIsSaving(false);
    }
  }, [apiClient, localTradeLimits, onClose, updateUserSettings, localDefaultSizes, isDefaultSizeValidAll]);

  const handleCancel = useCallback(() => {
    setLocalTradeLimits(tradeLimits);
    setLocalDefaultSizes(
      new Map(tradeLimits.map(limit => [limit.symbol, userSettings?.ocl_trading?.default_trade_sizes?.[limit.symbol] ?? DEFAULT_TRADE_SIZE]))
    );
    onClose();
  }, [tradeLimits, onClose, userSettings]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '80vh',
          width: '900px',
          margin: 'auto',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontWeight="bold">Rearrange Exchange Order</Typography>
        <Typography variant="body2" color="text.secondary">
          Drag and drop to reorder exchanges within each product. The first exchange will be the default in trading widgets.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <DndContext
          onDragStart={event => {
            setDraggedItem(event.active.id as string);
          }}
          onDragEnd={event => {
            setDraggedItem(null);
            handleDragEnd(event);
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 2,
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            {localTradeLimits.map((limit, index) => (
              <ProductItem
                key={`product-${index}`}
                limit={limit}
                index={index}
                localDefaultSizes={localDefaultSizes}
                onDefaultSizeUpdate={onDefaultSizeUpdate}
                isDefaultSizeValid={isDefaultSizeValid(index)}
              />
            ))}
          </Box>

          <DragOverlay>
            {draggedItem ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {(() => {
                    const parts = draggedItem.split('-');
                    const productIndex = parseInt(parts[0]);
                    const exchangeIndex = parseInt(parts[1]);
                    const exchange = localTradeLimits[productIndex]?.exchange[exchangeIndex];
                    return exchange ? exchange.toUpperCase() : 'Exchange';
                  })()}
                </Typography>
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving || !isDefaultSizeValidAll}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
